/**
 * Node Registry (Canvas V2)
 *
 * Central registry for all canvas node types.
 * Maps type strings to React components (wrapped with error boundaries),
 * schema objects, and provides helper functions.
 *
 * Key V2 changes from V1:
 * - crypto.randomUUID() for node IDs (BUILD_ORDER Q2)
 * - Generated schemas from schemas/node_defs.py (BUILD_ORDER Q4)
 * - No static nodeCategories map (BUILD_ORDER Q5)
 * - PlaceholderNode for types not yet ported to canvas-v2
 */

import { memo, useEffect } from 'react'
import { NODE_CARD_RADIUS_CLASS } from './nodes/shared/constants'
import { Trans, useTranslation } from 'react-i18next'
import { Position, type NodeProps } from 'reactflow'
import { AlertTriangle } from 'lucide-react'
import { withNodeErrorBoundary } from './NodeErrorBoundary'
import { TypedHandle } from './TypedHandle'
import { createDebug } from '../../utils/debugLog'
import { useNodePresentation } from './NodePresentationContext'
import {
  withVideoGeneratorCreationAudioDefaults,
  type VideoGeneratorCreationAudioDefaultOptions,
} from '../../utils/videoGeneratorAudioDefaults'
import {
  NODE_SCHEMAS_BY_TYPE,
  type AppNodeData,
  type FeNodeType,
  type GeneratedNodeSchemaDef,
  type JsonValue,
  type ReactFlowCanvasNodeDataByType,
  type ReactFlowNodeDataPatch,
  type ReactFlowNodeRef,
  type YDocWriteRecord,
} from '../../generated/nodeDataTypes'

const sizeLog = createDebug('dw:size:registry')
const unknownLog = createDebug('dw:canvas:unknown-node')

// Sprint 3: Designer node components
import { CharacterDesignerNode } from './nodes/CharacterDesignerNode'
import { LocationDesignerNode } from './nodes/LocationDesignerNode'
import { ObjectDesignerNode } from './nodes/ObjectDesignerNode'

// Sprint 4: Upload + Display node components
import { ImageUploadNode } from './nodes/ImageUploadNode'

// Sprint 6: Image Generation nodes
import { ImageGeneratorNode } from './nodes/ImageGeneratorNode'
import { VideoUploadNode } from './nodes/VideoUploadNode'
import { AudioUploadNode } from './nodes/AudioUploadNode'

// Sprint 7: Video Generation node
import { VideoGeneratorNode } from './nodes/VideoGeneratorNode'

// Sprint 11: Sound Expert node
import { SoundExpertNode } from './nodes/SoundExpertNode'

// Sprint 18b: Video Editor node
import { VideoEditorNode } from './nodes/VideoEditorNode'

// Sprint 20: Script Writer node
import { ScriptWriterNode } from './nodes/ScriptWriterNode'

// Group node
import { GroupNode } from './nodes/GroupNode'

// Storyboard Designer node
import { StoryboardDesignerNode } from './nodes/StoryboardDesignerNode'

// =============================================================================
// Types
// =============================================================================

type SchemaValue = JsonValue | undefined

export interface PortDef {
  id: string
  type: string
  label: string
  description?: string
  required?: boolean
  multi?: boolean
  default?: SchemaValue
  options?: readonly JsonValue[]
  hidden_from_director?: boolean
  hiddenFromDirector?: boolean
  hidden?: boolean
  display?: string
  backendField?: string
  pythonType?: string
  tsType?: string
  wireFormat?: string
  portSchemaId?: string
}

export type CanvasNodeRef<T extends FeNodeType> = ReactFlowNodeRef<T>
export type NodeDataRecord<T extends FeNodeType> = ReactFlowNodeDataPatch<T>
export type RawNodeDataValue = YDocWriteRecord[string]
export type RawNodeDataRecord = YDocWriteRecord
type SchemaConfig = Readonly<Record<string, JsonValue>>

export interface NodeSchema {
  type: string
  label: string
  category: string
  icon?: string
  description?: string
  inputs: readonly PortDef[]
  outputs: readonly PortDef[]
  defaults: Record<string, SchemaValue>
  backendNodeId?: string
  backendType?: string
  executable?: boolean
  layout?: Readonly<Record<string, { readonly width: number; readonly height: number }>>
  footerHeight?: number
  config?: readonly SchemaConfig[]
}

type RawNodeSchema = GeneratedNodeSchemaDef

// =============================================================================
// Schema Derivation
// =============================================================================

/**
 * Maps a port definition to its data field name.
 * The port ID IS the data field name — no translation needed.
 * @deprecated Use port.id directly. Kept for backward compatibility.
 */
export function portIdToDataField(input: { id: string }): string {
  return input.id
}

/**
 * Enrich a raw JSON schema with computed defaults.
 */
function enrichSchema(raw: RawNodeSchema): NodeSchema {
  const inputs = raw.inputs ?? []
  const outputs = raw.outputs ?? []
  const defaults: Record<string, SchemaValue> = {}
  for (const input of inputs) {
    if ('default' in input) {
      defaults[portIdToDataField(input)] = input.default
    }
  }
  return {
    ...raw,
    inputs,
    outputs,
    defaults,
  }
}

// =============================================================================
// Schema Registry (generated from schemas/node_defs.py)
// =============================================================================

export const nodeSchemas: Record<string, NodeSchema> = Object.fromEntries(
  Object.entries(NODE_SCHEMAS_BY_TYPE).map(([type, schema]) => [type, enrichSchema(schema)])
)

// =============================================================================
// Placeholder Node (renders for types not yet ported to canvas-v2)
// =============================================================================

type PlaceholderNodeData = RawNodeDataRecord & {
  status?: string
}

const PlaceholderNode = memo(function PlaceholderNode({ id, data, type }: NodeProps<PlaceholderNodeData>) {
  const schema = type ? nodeSchemas[type] : undefined
  const label = schema?.label || type || 'Unknown'
  const icon = schema?.icon || '\u{1F4E6}'

  return (
    <div className={`${NODE_CARD_RADIUS_CLASS} shadow bg-card border border-border min-w-[160px] max-w-[280px]`}>
      <div className="px-3 py-2 bg-muted/50 border-b border-border rounded-t-lg flex items-center gap-2">
        <span className="text-sm">{icon}</span>
        <span className="text-xs font-medium text-foreground truncate">{label}</span>
        <div
          className={`ml-auto w-2 h-2 rounded-full ${
            data?.status === 'running' ? 'bg-yellow-500 animate-pulse' :
            data?.status === 'error' ? 'bg-red-500' :
            data?.status === 'completed' ? 'bg-green-500' :
            'bg-gray-500'
          }`}
        />
      </div>

      {/* Input handles */}
      {schema?.inputs.map((port, i) => (
        <TypedHandle
          key={port.id}
          nodeId={id}
          type="target"
          position={Position.Left}
          id={port.id}
          portType={port.type}
          style={{ top: `${((i + 1) / (schema.inputs.length + 1)) * 100}%` }}
        />
      ))}

      {/* Output handles */}
      {schema?.outputs.map((port, i) => (
        <TypedHandle
          key={port.id}
          nodeId={id}
          type="source"
          position={Position.Right}
          id={port.id}
          portType={port.type}
          style={{ top: `${((i + 1) / (schema.outputs.length + 1)) * 100}%` }}
        />
      ))}

      <div className="px-3 py-2">
        <p className="text-xxs text-muted-foreground">
          {schema?.description || `${label} node`}
        </p>
      </div>
      {id && (
        <div className="px-3 pb-2">
          <p className="text-xxs text-foreground/20 font-mono truncate">{id}</p>
        </div>
      )}
    </div>
  )
})

// =============================================================================
// Unknown Node (fallback for types not in nodeSchemas)
// =============================================================================

const UnknownNode = memo(function UnknownNode(props: NodeProps<RawNodeDataRecord>) {
  const { type, id, data } = props
  const { t } = useTranslation('canvas')
  // NAD logging — surface the unknown type so we can fix the registry / schema.
  useEffect(() => {
    unknownLog({
      node_id: id,
      type,
      data_keys: Object.keys(data ?? {}),
    })
  }, [id, type, data])
  return (
    <div className={`${NODE_CARD_RADIUS_CLASS} shadow bg-card border border-destructive/50 min-w-[200px] max-w-[280px]`}>
      <div className="px-3 py-2 bg-destructive/10 border-b border-destructive/30 rounded-t-lg flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-destructive" aria-hidden="true" />
        <span className="text-xs font-medium text-destructive">{t('nodeRegistry.unknownNode')}</span>
      </div>
      <div className="px-3 py-3">
        <p className="text-xs font-medium text-text-secondary">
          <Trans
            i18nKey="nodeRegistry.nodeTypeNotRecognized"
            ns="canvas"
            values={{ type }}
            components={{ code: <code className="text-xxs bg-surface-2 px-1 py-0.5 rounded" /> }}
          />
        </p>
        <p className="text-xxs text-text-muted mt-1">
          {t('nodeRegistry.deleteOrUpdate')}
        </p>
      </div>
    </div>
  )
})

// =============================================================================
// Node Types Registry (component map for React Flow)
// =============================================================================

type CanvasNodeComponent = React.ComponentType<NodeProps<AppNodeData>>

function withNodePresentation(Component: CanvasNodeComponent): CanvasNodeComponent {
  return function PresentedCanvasNode(props: NodeProps<AppNodeData>) {
    const presentation = useNodePresentation()
    // Keep the canonical canvas instance alive to finish uploads and retain its
    // geometry. Hidden instances must not open panels or own selected shortcuts.
    return <Component {...props} selected={presentation === 'background' ? false : props.selected} />
  }
}

function canvasNodeComponent<T extends FeNodeType>(
  _type: T,
  component: React.ComponentType<NodeProps<ReactFlowCanvasNodeDataByType[T]>>,
): CanvasNodeComponent {
  return component as unknown as CanvasNodeComponent
}

function fallbackNodeComponent<TData>(
  component: React.ComponentType<NodeProps<TData>>,
): CanvasNodeComponent {
  return component as unknown as CanvasNodeComponent
}

function buildNodeTypes(): Record<string, CanvasNodeComponent> {
  const types: Record<string, CanvasNodeComponent> = {}

  // Map of node types that have real V2 implementations
  const v2Components: Record<string, CanvasNodeComponent> = {
    characterDesigner: canvasNodeComponent('characterDesigner', CharacterDesignerNode),
    locationDesigner: canvasNodeComponent('locationDesigner', LocationDesignerNode),
    objectDesigner: canvasNodeComponent('objectDesigner', ObjectDesignerNode),
    image: canvasNodeComponent('image', ImageUploadNode),
    imageGenerator: canvasNodeComponent('imageGenerator', ImageGeneratorNode),
    video: canvasNodeComponent('video', VideoUploadNode),
    audio: canvasNodeComponent('audio', AudioUploadNode),
    generatedImageWithHistory: canvasNodeComponent('generatedImageWithHistory', ImageGeneratorNode),
    videoGenerator: canvasNodeComponent('videoGenerator', VideoGeneratorNode),
    soundExpert: canvasNodeComponent('soundExpert', SoundExpertNode),
    videoEditor: canvasNodeComponent('videoEditor', VideoEditorNode),
    scriptWriter: canvasNodeComponent('scriptWriter', ScriptWriterNode),
    nodeGroup: canvasNodeComponent('nodeGroup', GroupNode),
    storyboardDesigner: canvasNodeComponent('storyboardDesigner', StoryboardDesignerNode),
    storyboardNoEnhanceDesigner: canvasNodeComponent('storyboardNoEnhanceDesigner', StoryboardDesignerNode),
  }

  for (const [key, schema] of Object.entries(nodeSchemas)) {
    const component = v2Components[key] || fallbackNodeComponent(PlaceholderNode)
    // schema.label = human header text; key = stable camelCase registry key used
    // as the analytics node_type (RFC-0974 §5.3).
    types[key] = withNodeErrorBoundary(withNodePresentation(component), schema.label, key)
  }
  // Fallback for node types not in nodeSchemas (e.g. removed or from a newer version)
  types.default = withNodeErrorBoundary(fallbackNodeComponent(UnknownNode), 'Unknown', 'unknown')
  return types
}

export const nodeTypes = buildNodeTypes()

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Get schema for a specific node type.
 * Returns undefined for unknown types.
 */
export function getNodeSchema(type: string): NodeSchema | undefined {
  return nodeSchemas[type]
}

/**
 * Get all nodes grouped by their schema category field.
 * This is the authoritative grouping (BUILD_ORDER Q5).
 */
export function getNodesByCategory(): Record<string, NodeSchema[]> {
  const grouped: Record<string, NodeSchema[]> = {}
  for (const schema of Object.values(nodeSchemas)) {
    const cat = schema.category || 'General'
    if (!grouped[cat]) {
      grouped[cat] = []
    }
    grouped[cat].push(schema)
  }
  return grouped
}

/**
 * List all available node type strings.
 */
export function getAvailableNodeTypes(): string[] {
  return Object.keys(nodeSchemas)
}

export interface CreatedCanvasNode {
  id: string
  type: string
  position: { x: number; y: number }
  data: RawNodeDataRecord
}

/**
 * Create a new React Flow node object with schema defaults.
 * Uses crypto.randomUUID() for IDs (BUILD_ORDER Q2).
 *
 * @throws Error if the type has no schema
 */
export function createNode(
  type: string,
  position: { x: number; y: number },
  additionalData: RawNodeDataRecord = {},
  audioDefaultOptions: VideoGeneratorCreationAudioDefaultOptions = {},
  id?: string,
): CreatedCanvasNode {
  const schema = nodeSchemas[type]
  if (!schema) {
    throw new Error(`Unknown node type: ${type}`)
  }

  return {
    id: id ?? crypto.randomUUID(),
    type,
    position,
    data: withVideoGeneratorCreationAudioDefaults(
      type,
      {
        ...schema.defaults,
        status: 'idle',
        ...additionalData,
      },
      audioDefaultOptions,
    ),
  }
}

/**
 * Get the backend executor ID for a frontend node type.
 * Checks backendNodeId/backendType on schema, else converts camelCase to snake_case.
 */
export function getBackendNodeId(nodeType: string): string {
  const schema = nodeSchemas[nodeType]
  if (schema) {
    if (schema.backendNodeId) return schema.backendNodeId
    if (schema.backendType) return schema.backendType
  }
  // Fallback: convert camelCase to snake_case
  return nodeType.replace(/([A-Z])/g, '_$1').toLowerCase().replace(/^_/, '')
}

/**
 * Check if a node type is executable (has a backend executor).
 * Returns false for unknown types.
 */
export function isNodeExecutable(nodeType: string): boolean {
  const schema = nodeSchemas[nodeType]
  return schema?.executable === true
}

/**
 * Get the layout dimensions for a node type.
 * Looks up the aspect-ratio-specific size first, then falls back to '*' wildcard.
 * Returns a default fallback if the type has no schema or no layout.
 * Also returns footerHeight (per-type footer chrome height, 0 if absent).
 */
export function getNodeDimensions(
  type: string,
  aspectRatio?: string,
): { width: number; height: number; footerHeight: number } {
  const schema = nodeSchemas[type]
  const footerHeight = schema?.footerHeight ?? 0
  if (!schema?.layout) {
    sizeLog('[size] getNodeDimensions: type=%s ratio=%s -> 315x300 (no schema/layout)', type, aspectRatio)
    return { width: 315, height: 300, footerHeight }
  }

  let result: { width: number; height: number; footerHeight: number }
  if (aspectRatio && schema.layout[aspectRatio]) {
    result = { ...schema.layout[aspectRatio], footerHeight }
  } else if (schema.layout['*']) {
    result = { ...schema.layout['*'], footerHeight }
  } else {
    const entries = Object.values(schema.layout)
    if (entries.length > 0) {
      result = { ...entries[0], footerHeight }
    } else {
      result = { width: 315, height: 300, footerHeight }
    }
  }

  sizeLog('[size] getNodeDimensions: type=%s ratio=%s -> %dx%d', type, aspectRatio, result.width, result.height)
  return result
}
